import { DashboardRoutes } from "@/router/constant";
import { Alert, Button, Col, Empty, Flex, Row, Skeleton, Typography } from "@payping/web-components";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useIbans } from "@/pages/Settings/Bank/hooks/useIbans";
import { MerchantIbanStatusEnum } from "@payping/api/app/api/terminalmanagerTypes";
import { useAppSelector } from "@/utils";
import { PhRHourglass } from "@payping/icons";

import ShebaCard from "./components/ShebaCard";

const PendingReviewState = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  return (
    <Flex
      className="tw:bg-white tw:my-8! tw:w-full tw:rounded-lg tw:border tw:border-solid tw:p-4!"
      style={{ borderColor: "#D9D9D9" }}
    >
      <div className="tw:mb-6! tw:flex tw:justify-center">
        <PhRHourglass className="color--warning" />
      </div>
      <Flex vertical align="start" gap="8px" className="tw:w-full tw:text-right">
        <Typography.Title level={4} className="tw:mb-0! tw:text-right" style={{ color: "#000000E0" }}>
          {t("profile.bank.pendingReviewTitle")}
        </Typography.Title>

        <Typography.Text className="tw:mb-0! tw:block tw:text-right" style={{ color: "#00000080" }}>
          {t("profile.bank.pendingReviewDescription")}
        </Typography.Text>

        <Button
          type="link"
          onClick={() => navigate(DashboardRoutes.settings.bank.edit.url)}
          className="tw:text-blue-600! tw:mt-1! tw:self-end"
        >
          {t("profile.bank.viewAccountDetails")}
        </Button>
      </Flex>
    </Flex>
  );
};

const UserBank: React.FC = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const ibanList = useAppSelector((state) => state.terminal.ibans.list?.items);
  const businessSettingIban = useAppSelector((state) => state.withdraws.businessSetting.iban);
  const isSettlement = useAppSelector((state) => state.user.isSettlement);
  const matchingIBAN = businessSettingIban ? ibanList?.find((iban) => iban.iban === businessSettingIban) : undefined;
  const { loading } = useIbans();

  const acceptedCount = (ibanList || []).filter((item) => item.status === MerchantIbanStatusEnum.Accepted).length;
  const pendingCount = (ibanList || []).filter((item) => item.status === MerchantIbanStatusEnum.Pending).length;

  const isPendingReviewState = !matchingIBAN?.iban && pendingCount > 0 && acceptedCount === 0;
  const shouldShowAlert = !loading && Boolean(ibanList?.length || matchingIBAN?.iban) && !isPendingReviewState;

  return (
    <Row className="tw:w-full">
      <Col span={24}>
        <Typography.Title className="tw:mb-8!" level={4}>
          {t("profile.main.bank")}
        </Typography.Title>

        {shouldShowAlert ? (
          <Alert type="warning" title={t("profile.bank.settlementTimeWarning")} showIcon={false} className="tw:mb-6!" />
        ) : null}

        {loading ? (
          <Skeleton active paragraph />
        ) : matchingIBAN?.iban ? (
          <ShebaCard iban={matchingIBAN?.iban} fieldData={{ name: 0, key: 0 }} />
        ) : !matchingIBAN?.iban && pendingCount > 0 && acceptedCount === 0 ? (
          <PendingReviewState />
        ) : !matchingIBAN?.iban && (pendingCount === 0 || acceptedCount > 0) ? (
          <Empty description={t("profile.noSheba")} />
        ) : // <EmptyBankState onAddNew={() => setShowAddIBANModal(true)} />
        null}

        <Button
          onClick={isSettlement ? undefined : () => navigate(DashboardRoutes.settings.bank.edit.url)}
          loading={loading}
          disabled={isSettlement}
          className="tw:text-blue-600 tw:mt-6! tw:w-full tw:md:w-fit"
        >
          {t("profile.bank.edit")}
        </Button>
        <div className="tw:bg-gray-50 tw:mt-2! tw:rounded-lg tw:px-0! tw:md:mt-6! tw:md:p-4!">
          <Typography.Text className="tw:text-gray-700 tw:whitespace-pre-line tw:text-sm">
            {t("profile.bank.fullInformationalText")}
          </Typography.Text>
        </div>
      </Col>
    </Row>
  );
};

export default UserBank;
